import { Swiper, SwiperSlide } from "swiper/react";
import Image from "next/image";
import { useState, useRef } from "react";
import type { Swiper as SwiperType } from "swiper";

import { dispatch } from "@shared/utils.events";
import {
  CloseIcon,
  ContractIcon,
  ExpandIcon,
  PlayIcon
} from "@shared/ui.icons";
import { colours } from "@shared/ui.design-system";

import { isVideoType } from "../utils/utils";
import { VideoPlayer } from "components/VideoPlayer";
import { isStorybook } from "utils/storybookUtils";
import { useWindowSize } from "hooks";
import type { Media } from "@shared/graphql.types";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/thumbs";
import "swiper/css/zoom";

import * as styles from "./GalleryZoom.css";
import { cloudinaryLoader } from "@shared/utils.cloudinary";

type Props = {
  zoomIndex: number;
  onZoomChange: (swiper: SwiperType, index: number) => void;
  media: Media[];
  thumbsSize: number;
  isZoomed: boolean;
  hideZoomMode: () => void;
  useCloudinary?: boolean;
};

export const GalleryZoom = ({
  zoomIndex,
  onZoomChange,
  media,
  thumbsSize,
  isZoomed,
  hideZoomMode,
  useCloudinary
}: Props) => {
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType | null>(null);
  const [zoomSwiper, setZoomSwiper] = useState<SwiperType | null>(null);
  const zoomButtonRef = useRef<HTMLButtonElement>(null);
  const { width } = useWindowSize();

  return (
    <div className={styles.container} data-testid="ZoomContainer">
      <div className={styles.swiperContainer}>
        <Swiper
          onZoomChange={onZoomChange}
          thumbs={{ swiper: thumbsSwiper }}
          zoom
          initialSlide={zoomIndex}
          allowTouchMove={false}
          onInit={(swiper) => {
            setZoomSwiper(swiper);
          }}
          onSlideChange={(zoomSwiperInstance) => {
            dispatch("swiper.onslidechange", {
              target: "PDP Gallery Zoomed",
              position: zoomSwiperInstance?.realIndex
            });
            const isVideoSlide = isVideoType(
              media[zoomSwiperInstance.activeIndex]
            );
            const displayVal = isVideoSlide ? "none" : "flex";
            zoomButtonRef.current?.style.setProperty("display", displayVal);
          }}
        >
          {media?.map((item, index) => {
            if (isVideoType(item)) {
              return (
                <SwiperSlide
                  key={`zoom-video-player-${index}`}
                  style={{
                    width: "100%",
                    height: "100%"
                  }}
                >
                  <VideoPlayer
                    options={{
                      html5: {
                        nativeTextTracks: false
                      },
                      playsinline: true,
                      autoplay: false,
                      controls: true,
                      responsive: true,
                      aspectRatio: "1:1",
                      sources: item.sources.map(({ uri, contentType }) => ({
                        src: uri,
                        type: contentType
                      }))
                    }}
                    description={item.description ?? ""}
                    videoProps={{
                      title: item?.title ?? "",
                      "aria-label": item.title ?? "",
                      id: "gallery-video-player",
                      className:
                        "video-js vjs-big-play-centered vjs-show-big-play-button-on-pause gallery-video gallery-video-zoom"
                    }}
                    coverImageProps={{
                      src: item.imageUrl,
                      title: item.title ?? "",
                      alt: item.title ?? "",
                      style: { objectFit: "contain" }
                    }}
                    height="100%"
                    videoPosition="PDP gallery zoom"
                    pauseWhenNotInView={true}
                    autoPlayWhenScrolledIntoView={true}
                    muted={true}
                  />
                </SwiperSlide>
              );
            }
            return (
              <SwiperSlide
                key={`zoom-image-${index}`}
                data-testid="swiper-slide"
                style={{
                  width: "100%",
                  height: "100%"
                }}
              >
                <div className="swiper-zoom-container" data-swiper-zoom="3">
                  <img
                    data-testid="bynder-image"
                    src={item?.src ?? ""}
                    alt={item?.alt ?? ""}
                    className={styles.thumbImage}
                  />
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>

        <div className={styles.thumbsContainer} data-testid="ThumbsContainer">
          <div className={styles.thumbsSwiperContainer}>
            <Swiper
              onSwiper={setThumbsSwiper}
              freeMode
              initialSlide={zoomIndex}
              spaceBetween={0}
              slidesPerView={width / thumbsSize - 1}
              watchSlidesProgress
            >
              {media?.length &&
                media?.map((item, index) => {
                  if (isVideoType(item)) {
                    return (
                      <SwiperSlide
                        className="thumbs-swiper-slide"
                        key={`video-thumbnail-${index}`}
                        style={{
                          width: thumbsSize,
                          height: thumbsSize,
                          position: "relative"
                        }}
                        onClick={() => {
                          dispatch("page element clicked", {
                            target: "PDP - Gallery Expanded Thumbnails",
                            details: `Thumbnail Clicked - video`,
                            location: "PDP - Gallery",
                            locationDetails: item.id
                          });
                        }}
                      >
                        <PlayIcon
                          width={thumbsSize / 2}
                          height={thumbsSize / 2}
                          className={styles.playIcon}
                        />
                        <Image
                          data-testid="next-image"
                          style={{ objectFit: "contain" }}
                          src={item.imageUrl}
                          alt={item.title ?? ""}
                          width={thumbsSize}
                          height={thumbsSize}
                          unoptimized={isStorybook}
                          loader={useCloudinary ? cloudinaryLoader : undefined}
                        />
                      </SwiperSlide>
                    );
                  }
                  return (
                    <SwiperSlide
                      className="thumbs-swiper-slide"
                      key={`thumbnail-${index}`}
                      style={{
                        width: thumbsSize,
                        height: thumbsSize
                      }}
                      onClick={() => {
                        dispatch("page element clicked", {
                          target: "PDP - Gallery Expanded Thumbnails",
                          details: `Thumbnail Clicked - image`,
                          location: "PDP - Gallery",
                          locationDetails: item.src
                        });
                      }}
                    >
                      <Image
                        data-testid="next-image"
                        style={{ objectFit: "contain" }}
                        src={item?.src as string}
                        alt={item?.alt ?? ""}
                        width={thumbsSize}
                        height={thumbsSize}
                        unoptimized={isStorybook}
                        loader={useCloudinary ? cloudinaryLoader : undefined}
                      />
                    </SwiperSlide>
                  );
                })}
            </Swiper>
            <button
              className={styles.closeButton}
              style={{
                width: `${thumbsSize}px`,
                height: `${thumbsSize}px`,
                minWidth: `${thumbsSize}px`
              }}
              onClick={hideZoomMode}
            >
              <CloseIcon width={32} height={32} color={colours.primary.dark} />
            </button>
          </div>
        </div>
      </div>

      <button
        className={styles.zoomButton}
        ref={zoomButtonRef}
        style={{
          bottom: `${thumbsSize}px`,
          display: isVideoType(media[zoomIndex]) ? "none" : "flex"
        }}
        onClick={() => {
          if (zoomSwiper) {
            zoomSwiper.zoom.toggle();
          }
        }}
      >
        {isZoomed ? (
          <ContractIcon width={24} height={24} color={colours.primary.dark} />
        ) : (
          <ExpandIcon width={24} height={24} color={colours.primary.dark} />
        )}
      </button>
    </div>
  );
};
